import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, X, Save, ExternalLink, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// Column → accent colour mapping. Pre-approval columns are neutral; the
// approval-gated columns (Approved/Scheduled/Posted/Repurposed) are coloured
// so the page reads at a glance.
const COLUMN_ACCENTS = {
  'Idea':              'bg-slate-100 dark:bg-slate-800',
  'Drafting':          'bg-slate-100 dark:bg-slate-800',
  'Needs review':      'bg-crm-amber/15',
  'Changes required':  'bg-crm-red/10',
  'Approved':          'bg-emerald-500/10',
  'Scheduled':         'bg-crm-teal/15',
  'Posted':            'bg-crm-blue/10',
  'Repurposed':        'bg-purple-500/10',
};

const ApprovalBoard = ({ auth, options, isAdmin, onChange }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ broker: '', platform: '', status: '' });
  const [editing, setEditing] = useState(null);    // card being edited in modal
  const [creating, setCreating] = useState(false); // "Add card" form open
  const [dragId, setDragId] = useState(null);

  const columns = options?.board_columns || ['Idea', 'Drafting', 'Needs review', 'Changes required', 'Approved', 'Scheduled', 'Posted', 'Repurposed'];

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && qs.append(k, v));
    const r = await fetch(`${API}/api/media/cards?${qs}`, { headers: auth });
    setCards(await r.json());
    setLoading(false);
  }, [auth, filters]);

  useEffect(() => { load(); }, [load]);

  // Derive unique brokers/platforms from the loaded cards for filter dropdowns.
  // Plain arrays — cheap enough not to need memoisation.
  const brokerSet = Array.from(new Set(cards.map(c => c.broker).filter(Boolean)));
  const platformSet = Array.from(new Set(cards.map(c => c.platform).filter(Boolean)));

  // Drag and drop ───────────────────────────────────────────────────────────
  const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!dragId) return;
    const card = cards.find(c => c.id === dragId);
    setDragId(null);
    if (!card || card.status === newStatus) return;
    // Optimistic update
    setCards(prev => prev.map(c => c.id === dragId ? { ...c, status: newStatus } : c));
    const r = await fetch(`${API}/api/media/cards/${dragId}`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!r.ok) {
      // Revert + tell the user (typically a 403 for non-admin moving into approval-gated columns)
      setCards(prev => prev.map(c => c.id === dragId ? { ...c, status: card.status } : c));
      const msg = r.status === 403 ? 'Only admins can move cards into this column' : 'Failed to update card';
      toast.error(msg);
      return;
    }
    toast.success(`Moved to "${newStatus}"`);
    onChange?.();
    load();
  };

  // Cards grouped by column (re-computed each render — cheap for <500 cards)
  const grouped = (() => {
    const acc = Object.fromEntries(columns.map(c => [c, []]));
    cards.forEach(c => {
      const col = acc[c.status] ? c.status : 'Idea';
      acc[col].push(c);
    });
    return acc;
  })();

  return (
    <div className="space-y-4" data-testid="approval-board">
      {/* Filter + add bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex flex-wrap gap-2 flex-1">
          <Select value={filters.broker || 'all'} onValueChange={v => setFilters(f => ({ ...f, broker: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="All brokers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brokers</SelectItem>
              {brokerSet.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.platform || 'all'} onValueChange={v => setFilters(f => ({ ...f, platform: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="All platforms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {platformSet.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-44 h-9 text-xs"><SelectValue placeholder="All columns" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All columns</SelectItem>
              {columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="new-card-btn">
          <Plus className="w-4 h-4 mr-1" /> New card
        </Button>
      </div>

      {creating && (
        <CardForm auth={auth} options={options} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); onChange?.(); }} />
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-text-muted" /></div>
      ) : (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3 min-w-max">
            {columns.map(col => (
              <div
                key={col}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, col)}
                className={`w-72 shrink-0 rounded-xl p-2 ${COLUMN_ACCENTS[col] || 'bg-slate-100 dark:bg-slate-800'}`}
                data-testid={`board-col-${col.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center justify-between px-2 py-1.5">
                  <h3 className="text-[11px] uppercase tracking-widest font-bold text-text-main">{col}</h3>
                  <span className="text-[11px] text-text-muted tabular-nums">{grouped[col]?.length || 0}</span>
                </div>
                <div className="space-y-2 min-h-[40px]">
                  {(grouped[col] || []).map(card => (
                    <button
                      key={card.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, card.id)}
                      onClick={() => setEditing(card)}
                      className="w-full text-left bg-surface border border-surface-border rounded-lg p-2.5 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
                      data-testid={`board-card-${card.id}`}
                    >
                      <p className="text-sm font-semibold text-text-main line-clamp-2 leading-tight">{card.title}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5 text-[9px] uppercase tracking-widest font-bold text-text-muted">
                        {card.platform && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700">{card.platform}</span>}
                        {card.content_type && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700">{card.content_type}</span>}
                      </div>
                      {card.due_date && (
                        <p className="text-[10px] text-text-muted mt-1.5">Due {card.due_date}</p>
                      )}
                      {card.broker && (
                        <p className="text-[10px] text-text-muted truncate">{card.broker}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <CardModal
          card={editing}
          auth={auth}
          options={options}
          isAdmin={isAdmin}
          columns={columns}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChange?.(); }}
        />
      )}
    </div>
  );
};

// ── Card create form ───────────────────────────────────────────────────────
const CardForm = ({ auth, options, onClose, onSaved }) => {
  const [f, setF] = useState({ title: '', platform: '', content_type: '', caption_draft: '', script_draft: '', due_date: '', creative_url: '', broker: '', campaign: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const r = await fetch(`${API}/api/media/cards`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    setSaving(false);
    if (r.ok) { toast.success('Card created in "Idea"'); onSaved(); }
    else toast.error('Failed to create card');
  };
  return (
    <form onSubmit={submit} className="bg-surface border border-surface-border rounded-2xl p-4 shadow-soft space-y-3" data-testid="card-form">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-text-main">New board card</h3>
        <button type="button" onClick={onClose} aria-label="Close"><X className="w-4 h-4 text-text-muted" /></button>
      </div>
      <Input placeholder="Title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="card-title" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={f.platform} onValueChange={v => setF({ ...f, platform: v })}>
          <SelectTrigger><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>{options?.content_formats?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.content_type} onValueChange={v => setF({ ...f, content_type: v })}>
          <SelectTrigger><SelectValue placeholder="Content type" /></SelectTrigger>
          <SelectContent>{options?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} />
        <Input placeholder="Campaign (optional)" value={f.campaign} onChange={e => setF({ ...f, campaign: e.target.value })} />
      </div>
      <Textarea placeholder="Caption draft" value={f.caption_draft} onChange={e => setF({ ...f, caption_draft: e.target.value })} rows={2} />
      <Input placeholder="Creative link (Canva / file URL)" value={f.creative_url} onChange={e => setF({ ...f, creative_url: e.target.value })} />
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="card-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create card'}
        </Button>
      </div>
    </form>
  );
};

// ── Card edit modal ────────────────────────────────────────────────────────
const CardModal = ({ card, auth, options, isAdmin, columns, onClose, onSaved }) => {
  const [f, setF] = useState({
    title: card.title || '', platform: card.platform || '', content_type: card.content_type || '',
    caption_draft: card.caption_draft || '', script_draft: card.script_draft || '',
    due_date: card.due_date || '', creative_url: card.creative_url || '',
    approval_notes: card.approval_notes || '', date_posted: card.date_posted || '',
    final_post_url: card.final_post_url || '', status: card.status || 'Idea',
    broker: card.broker || '', campaign: card.campaign || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const r = await fetch(`${API}/api/media/cards/${card.id}`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    setSaving(false);
    if (r.ok) { toast.success('Card saved'); onSaved(); }
    else if (r.status === 403) toast.error('Only admins can move into approval-gated columns');
    else toast.error('Failed to save');
  };

  const remove = async () => {
    if (!window.confirm('Delete this card? This cannot be undone.')) return;
    const r = await fetch(`${API}/api/media/cards/${card.id}`, { method: 'DELETE', headers: auth });
    if (r.ok) { toast.success('Card deleted'); onSaved(); }
    else toast.error('Only admins can delete');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="card-modal">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-surface border-b border-surface-border px-5 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-bold text-text-main">Edit board card</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-4 h-4 text-text-muted hover:text-text-main" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="text-base font-bold" placeholder="Title" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Select value={f.platform || 'unset'} onValueChange={v => setF({ ...f, platform: v === 'unset' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">— None —</SelectItem>
                {options?.content_formats?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.content_type || 'unset'} onValueChange={v => setF({ ...f, content_type: v === 'unset' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Content type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">— None —</SelectItem>
                {options?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} placeholder="Due date" />
            <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="modal-status"><SelectValue /></SelectTrigger>
              <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.broker} onChange={e => setF({ ...f, broker: e.target.value })} placeholder="Broker" />
            <Input value={f.campaign} onChange={e => setF({ ...f, campaign: e.target.value })} placeholder="Campaign" />
          </div>
          <Textarea value={f.caption_draft} onChange={e => setF({ ...f, caption_draft: e.target.value })} rows={3} placeholder="Caption draft" />
          <Textarea value={f.script_draft} onChange={e => setF({ ...f, script_draft: e.target.value })} rows={3} placeholder="Script draft" />
          <Input value={f.creative_url} onChange={e => setF({ ...f, creative_url: e.target.value })} placeholder="Creative link (Canva / file URL)" />
          {f.creative_url && (
            <a href={f.creative_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-crm-blue hover:underline">
              <ExternalLink className="w-3 h-3" /> Open creative
            </a>
          )}
          <Textarea value={f.approval_notes} onChange={e => setF({ ...f, approval_notes: e.target.value })} rows={2} placeholder="Approval notes (admin)" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input type="date" value={f.date_posted} onChange={e => setF({ ...f, date_posted: e.target.value })} placeholder="Date posted" />
            <Input value={f.final_post_url} onChange={e => setF({ ...f, final_post_url: e.target.value })} placeholder="Link to final post" />
          </div>

          {/* Comments thread */}
          <CardComments card={card} auth={auth} />
        </div>
        <div className="sticky bottom-0 bg-surface border-t border-surface-border px-5 py-3 flex items-center justify-between gap-2">
          {isAdmin && (
            <Button variant="ghost" onClick={remove} className="text-crm-red hover:bg-crm-red/10">Delete</Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="modal-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalBoard;

// ── CardComments ───────────────────────────────────────────────────────────
// Shared comment thread component — backend exposes the same endpoint shape
// for ideas / cards / requests (POST /api/media/{kind}/{id}/comments).
// `function` declaration (not `const`) so it's hoisted and CardModal above
// can reference it.
export function CardComments({ card, kind = 'cards', auth, onChange }) {
  const [comments, setComments] = useState(card.comments || []);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    const r = await fetch(`${API}/api/media/${kind}/${card.id}/comments`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    setSending(false);
    if (r.ok) {
      const c = await r.json();
      setComments(prev => [...prev, c]);
      setText('');
      onChange?.();
    } else {
      toast.error('Failed to post comment');
    }
  };

  return (
    <div className="pt-3 mt-1 border-t border-surface-border" data-testid="card-comments">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest font-bold text-text-muted">
        <MessageSquare className="w-3.5 h-3.5" />
        Comments ({comments.length})
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
        {comments.length === 0 ? (
          <p className="text-xs text-text-muted italic">No comments yet — start the thread.</p>
        ) : comments.map(c => (
          <div key={c.id} className="bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-bold text-text-main">{c.author_name || 'Unknown'}</span>
              <span className="text-[10px] text-text-muted">{c.created_at?.slice(0, 16).replace('T', ' ')}</span>
            </div>
            <p className="text-sm text-text-main whitespace-pre-wrap mt-0.5">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Write a comment…"
          className="flex-1"
          data-testid="comment-input"
        />
        <Button onClick={send} disabled={sending || !text.trim()} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="comment-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
